import { db } from '../firebase';
import {
  collection, doc, getDocs, deleteDoc, updateDoc, query, where, getCountFromServer,
} from 'firebase/firestore';

// ─── Operaciones compartidas entre módulos (sesión 34) ───────────────────────
// Una sola versión de cada borrado, usada por las fichas y por el Dashboard (solicitudes de eliminación).
// Solo el administrador puede ejecutarlas: las reglas de Firestore rechazan el borrado a cualquier otro rol.

// Estado agregado de una oportunidad a partir de los estados de TODOS sus presupuestos (regla sesiones 30 y 32)
export const estadoAgregadoOp = (estados) => {
  if (!estados || estados.length === 0) return 'pendiente';
  if (estados.includes('aceptado')) return 'aceptado';
  if (estados.includes('enviado')) return 'presupuestado';
  return 'rechazado';
};

// Estado agregado leído del servidor en este momento (2 o 3 lecturas). excluirId: presupuesto que se va a borrar.
export const estadoAgregadoServidor = async (oportunidadId, { excluirId = null } = {}) => {
  const snap = await getDocs(query(collection(db, 'presupuestos'), where('oportunidadId', '==', oportunidadId)));
  return estadoAgregadoOp(snap.docs.filter(d => d.id !== excluirId).map(d => d.data().estado));
};

const ETIQUETA_OP = { pendiente: 'Pendiente', presupuestado: 'Presupuestado', aceptado: 'Aceptado', rechazado: 'Rechazado' };
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const contar = async (q) => (await getCountFromServer(q)).data().count;
const borrarSub = async (ruta) => {
  const snap = await getDocs(collection(db, ...ruta));
  await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
  return snap.size;
};

/**
 * Texto de lo que se va a borrar, para la confirmación. Lee poco (recuentos y subcolecciones pequeñas).
 * tipo: 'lead' | 'presupuesto' | 'cliente' | 'proyecto' ('oportunidad' solo en ejecutarEliminacion). datos: el documento (para nombres y vínculos).
 */
export async function resumenEliminacion(tipo, id, datos = {}) {
  const lineas = [];
  if (tipo === 'lead') {
    const [ops, notas, pres] = await Promise.all([
      contar(collection(db, 'leads', id, 'posibles_proyectos')),
      contar(collection(db, 'leads', id, 'notas')),
      contar(query(collection(db, 'presupuestos'), where('leadId', '==', id))),
    ]);
    lineas.push(`Se eliminará el lead ${`${datos.nombre || ''} ${datos.apellidos || ''}`.trim()} con ${plural(ops, 'oportunidad', 'oportunidades')} y ${plural(notas, 'nota', 'notas')}.`);
    if (pres > 0) lineas.push(`ATENCIÓN: tiene ${plural(pres, 'presupuesto', 'presupuestos')} que quedarán sin lead.`);
  } else if (tipo === 'presupuesto') {
    lineas.push(`Se eliminará el presupuesto ${datos.num_presupuesto || ''}.`);
    if (datos.oportunidadId) {
      const estado = await estadoAgregadoServidor(datos.oportunidadId, { excluirId: id });
      lineas.push(`Su oportunidad pasará a «${ETIQUETA_OP[estado] || estado}».`);
    }
    if (datos.proyectoId) lineas.push('El proyecto creado a partir de él no se borra: solo pierde el vínculo con este presupuesto.');
  } else if (tipo === 'cliente') {
    const [notas, proys] = await Promise.all([
      contar(collection(db, 'clientes', id, 'notas')),
      contar(query(collection(db, 'proyectos'), where('clienteId', '==', id))),
    ]);
    const nombre = datos.tipo === 'empresa' ? datos.razon_social : `${datos.nombre || ''} ${datos.apellidos || ''}`.trim();
    lineas.push(`Se eliminará el cliente ${nombre || ''} con ${plural(notas, 'nota', 'notas')}.`);
    if (proys > 0) lineas.push(`ATENCIÓN: tiene ${plural(proys, 'proyecto', 'proyectos')} que quedarán sin cliente.`);
  } else if (tipo === 'proyecto') {
    const [notas, horas] = await Promise.all([
      contar(collection(db, 'proyectos', id, 'notas')),
      contar(query(collection(db, 'registros_horas'), where('proyectoId', '==', id))),
    ]);
    lineas.push(`Se eliminará el proyecto ${datos.codigo ?? ''} — ${datos.nombre || ''} con ${plural(notas, 'nota', 'notas')}.`);
    if (horas > 0) lineas.push(`ATENCIÓN: tiene ${plural(horas, 'registro de horas', 'registros de horas')} (${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(datos.horas_total || 0)} h) que quedarán sin proyecto.`);
    lineas.push('Los presupuestos vinculados quedarán libres para crear otro proyecto.');
  }
  lineas.push('Esta acción no se puede deshacer.');
  return lineas.join('\n');
}

/**
 * Borra el registro y sus dependientes, y deja coherentes los vínculos. Devuelve lo necesario para parchear la
 * memoria compartida: { oportunidadId, estadoOp } en presupuestos; { presupuestosLiberados } en proyectos.
 */
export async function ejecutarEliminacion(tipo, id, datos = {}) {
  if (tipo === 'lead') {
    await borrarSub(['leads', id, 'notas']);
    await borrarSub(['leads', id, 'posibles_proyectos']);
    await deleteDoc(doc(db, 'leads', id));
    return {};
  }
  if (tipo === 'presupuesto') {
    let estadoOp = null;
    if (datos.leadId && datos.oportunidadId) {
      estadoOp = await estadoAgregadoServidor(datos.oportunidadId, { excluirId: id });
      try {
        await updateDoc(doc(db, 'leads', datos.leadId, 'posibles_proyectos', datos.oportunidadId), { estado: estadoOp });
      } catch (e) { console.error('No se pudo actualizar el estado de la oportunidad (¿vínculo roto?)', e); estadoOp = null; }
    }
    // H15: el proyecto que nació de este presupuesto deja de apuntar a él
    if (datos.proyectoId) {
      try { await updateDoc(doc(db, 'proyectos', datos.proyectoId), { presupuestoId: '' }); }
      catch (e) { console.error('No se pudo limpiar el vínculo del proyecto', e); }
    }
    await deleteDoc(doc(db, 'presupuestos', id));
    return { oportunidadId: datos.oportunidadId || null, estadoOp, proyectoId: datos.proyectoId || null };
  }
  if (tipo === 'oportunidad') {
    // Sesión 34: nunca se borra una oportunidad con presupuestos (dejaría su vínculo roto). Se comprueba en el
    // servidor en el momento de borrar, no con la copia en memoria.
    if (!datos.leadId) throw new Error('Falta el lead de la oportunidad.');
    const n = await contar(query(collection(db, 'presupuestos'), where('oportunidadId', '==', id)));
    if (n > 0) throw new Error(`No se puede eliminar: tiene ${plural(n, 'presupuesto vinculado', 'presupuestos vinculados')}.`);
    await deleteDoc(doc(db, 'leads', datos.leadId, 'posibles_proyectos', id));
    return {};
  }
  if (tipo === 'cliente') {
    await borrarSub(['clientes', id, 'notas']);
    await deleteDoc(doc(db, 'clientes', id));
    return {};
  }
  if (tipo === 'proyecto') {
    await borrarSub(['proyectos', id, 'notas']);
    await deleteDoc(doc(db, 'proyectos', id));
    try { await deleteDoc(doc(db, 'proyectos_lista', id)); }
    catch (e) { console.error('No se pudo borrar de la lista reducida de proyectos', e); }
    const presSnap = await getDocs(query(collection(db, 'presupuestos'), where('proyectoId', '==', id)));
    const liberar = new Set([...presSnap.docs.map(d => d.id), ...(datos.presupuestoId ? [datos.presupuestoId] : [])]);
    for (const pid of liberar) {
      try { await updateDoc(doc(db, 'presupuestos', pid), { proyectoId: null }); }
      catch (e) { console.error('No se pudo liberar el presupuesto', pid, e); }
    }
    return { presupuestosLiberados: [...liberar] };
  }
  throw new Error(`Tipo de eliminación desconocido: ${tipo}`);
}
